import React from 'react';
import { useTheme } from '../../contexts/ThemeContext.jsx';

/**
 * Knowledge Base table component with sorting, pagination, and selection
 */
const KnowledgeBaseTable = ({
  knowledgeBaseFiles,
  loading,
  selectedKB,
  onKBSelect,
  pagination,
  onPageChange,
  onSort,
  onViewAssociations
}) => {
  const { theme } = useTheme();
  /**
   * Handle sort column click
   */
  const handleSort = (column) => {
    const currentSort = column;
    const newOrder = currentSort === column && pagination.sortOrder === 'asc' ? 'desc' : 'asc';
    onSort(column, newOrder);
  };

  /**
   * Format date for display
   */
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  /**
   * Format file size for display
   */
  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  /**
   * Get status badge styling
   */
  const getStatusBadge = (status) => {
    const baseClasses = 'px-2 py-1 rounded-full text-xs font-medium border';
    
    const darkStyles = {
      active: `${baseClasses} bg-green-900/30 text-green-400 border-green-500/30`,
      processing: `${baseClasses} bg-yellow-900/30 text-yellow-400 border-yellow-500/30`,
      error: `${baseClasses} bg-red-900/30 text-red-400 border-red-500/30`,
      inactive: `${baseClasses} bg-gray-900/30 text-gray-400 border-gray-500/30`,
      default: `${baseClasses} bg-blue-900/30 text-blue-400 border-blue-500/30`
    };

    const lightStyles = {
      active: `${baseClasses} bg-green-100 text-green-800 border-green-200`,
      processing: `${baseClasses} bg-yellow-100 text-yellow-800 border-yellow-200`,
      error: `${baseClasses} bg-red-100 text-red-800 border-red-200`,
      inactive: `${baseClasses} bg-gray-100 text-gray-800 border-gray-200`,
      default: `${baseClasses} bg-blue-100 text-blue-800 border-blue-200`
    };

    const styles = theme === 'dark' ? darkStyles : lightStyles;
    return styles[status?.toLowerCase()] || styles.default;
  };

  /**
   * Render sort icon
   */
  const SortIcon = ({ column, currentSort, sortOrder }) => {
    if (currentSort !== column) {
      return <span className={`ml-1 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>↕️</span>;
    }
    return (
      <span className="text-teal-500 ml-1">
        {sortOrder === 'asc' ? '↑' : '↓'}
      </span>
    );
  };

  /**
   * Render pagination controls
   */
  const PaginationControls = () => {
    const { page, totalPages } = pagination;
    const pages = [];
    
    // Calculate page range to show
    const startPage = Math.max(1, page - 2);
    const endPage = Math.min(totalPages, page + 2);
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <div className={`flex items-center justify-between px-4 py-3 border-t ${
        theme === 'dark' 
          ? 'bg-gray-800 border-gray-700' 
          : 'bg-gray-50 border-gray-200'
      }`}>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
            className={`px-3 py-1 text-sm rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              theme === 'dark'
                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
            }`}
          >
            Previous
          </button>
          
          {startPage > 1 && (
            <>
              <button
                onClick={() => onPageChange(1)}
                className={`px-3 py-1 text-sm rounded transition-colors ${
                  theme === 'dark'
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                }`}
              >
                1
              </button>
              {startPage > 2 && <span className={theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}>...</span>}
            </>
          )}
          
          {pages.map(pageNum => (
            <button
              key={pageNum}
              onClick={() => onPageChange(pageNum)}
              className={`px-3 py-1 text-sm rounded transition-colors ${
                pageNum === page
                  ? 'bg-teal-600 text-white'
                  : theme === 'dark'
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
              }`}
            >
              {pageNum}
            </button>
          ))}
          
          {endPage < totalPages && (
            <>
              {endPage < totalPages - 1 && <span className={theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}>...</span>}
              <button
                onClick={() => onPageChange(totalPages)}
                className={`px-3 py-1 text-sm rounded transition-colors ${
                  theme === 'dark'
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                }`}
              >
                {totalPages}
              </button>
            </>
          )}
          
          <button
            onClick={() => onPageChange(page + 1)}
            disabled={page >= totalPages}
            className={`px-3 py-1 text-sm rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              theme === 'dark'
                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
            }`}
          >
            Next
          </button>
        </div>
        
        <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
          Page {page} of {totalPages}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className={`rounded-lg border ${
        theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
      }`}>
        <div className="p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500 mx-auto"></div>
          <p className={`mt-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Loading Knowledge Base files...</p>
        </div>
      </div>
    );
  }

  if (!knowledgeBaseFiles || knowledgeBaseFiles.length === 0) {
    return (
      <div className={`rounded-lg border ${
        theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
      }`}>
        <div className="p-8 text-center">
          <div className="text-4xl mb-4">📚</div>
          <h3 className={`text-lg font-medium mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>No Knowledge Base files found</h3>
          <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
            No knowledge base files match your current filters. Try adjusting your search criteria.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-lg border overflow-hidden ${
      theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
    }`}>
      {/* Table header */}
      <div className={`px-4 py-3 border-b ${
        theme === 'dark' 
          ? 'bg-gray-900 border-gray-700' 
          : 'bg-gray-50 border-gray-200'
      }`}>
        <h3 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Knowledge Base Files ({pagination.total})</h3>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className={`border-b ${
            theme === 'dark' 
              ? 'bg-gray-900 border-gray-700' 
              : 'bg-gray-50 border-gray-200'
          }`}>
            <tr>
              <th 
                className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer transition-colors ${
                  theme === 'dark'
                    ? 'text-gray-300 hover:text-white'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => handleSort('name')}
              >
                Name
                <SortIcon column="name" currentSort={pagination.sortBy} sortOrder={pagination.sortOrder} />
              </th>
              <th 
                className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer transition-colors ${
                  theme === 'dark'
                    ? 'text-gray-300 hover:text-white'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => handleSort('user_name')}
              >
                Owner
                <SortIcon column="user_name" currentSort={pagination.sortBy} sortOrder={pagination.sortOrder} />
              </th>
              <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
              }`}>
                Status
              </th>
              <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
              }`}>
                Size
              </th>
              <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
              }`}>
                Associated Parrots
              </th>
              <th 
                className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer transition-colors ${
                  theme === 'dark'
                    ? 'text-gray-300 hover:text-white'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => handleSort('updated_at')}
              >
                Last Updated
                <SortIcon column="updated_at" currentSort={pagination.sortBy} sortOrder={pagination.sortOrder} />
              </th>
              <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
              }`}>
                Actions
              </th>
            </tr>
          </thead>
          <tbody className={`divide-y ${theme === 'dark' ? 'divide-gray-700' : 'divide-gray-200'}`}>
            {knowledgeBaseFiles.map((kb) => (
              <tr
                key={kb.id}
                className={`cursor-pointer transition-colors ${
                  selectedKB?.id === kb.id
                    ? theme === 'dark'
                      ? 'bg-teal-900/20 border-l-4 border-l-teal-400'
                      : 'bg-teal-50 border-l-4 border-l-teal-500'
                    : theme === 'dark'
                      ? 'hover:bg-gray-700/50'
                      : 'hover:bg-gray-50'
                }`}
                onClick={() => onKBSelect(kb)}
              >
                <td className="px-4 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-8 w-8">
                      <div className="h-8 w-8 rounded-full bg-blue-600 flex items-center justify-center">
                        <span className="text-sm font-medium text-white dark:text-white">
                          📚
                        </span>
                      </div>
                    </div>
                    <div className="ml-3">
                      <div className={`text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                        {kb.original_filename || 'Unnamed Knowledge Base'}
                      </div>
                      <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                        ID: {kb.id}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <div className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    {kb.owner?.name || kb.owner?.email || 'Unknown User'}
                  </div>
                  <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                    {kb.owner?.email}
                  </div>
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <span className={getStatusBadge(kb.status || 'active')}>
                    {kb.status || 'Active'}
                  </span>
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <div className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    {formatFileSize(kb.file_size)}
                  </div>
                  {kb.chunk_count && (
                    <div className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                      {kb.chunk_count} chunks
                    </div>
                  )}
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <div className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    {kb.usage_stats?.associated_parrots_count || 0} parrots
                  </div>
                  {kb.usage_stats?.associated_parrot_names?.length > 0 && (
                    <div className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                      {kb.usage_stats.associated_parrot_names.slice(0, 2).join(', ')}
                      {kb.usage_stats.associated_parrot_names.length > 2 && ` +${kb.usage_stats.associated_parrot_names.length - 2} more`}
                    </div>
                  )}
                </td>
                <td className={`px-4 py-4 whitespace-nowrap text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  {formatDate(kb.updated_at)}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onViewAssociations(kb);
                    }}
                    className={`mr-3 transition-colors ${
                      theme === 'dark' 
                        ? 'text-teal-400 hover:text-teal-300' 
                        : 'text-teal-600 hover:text-teal-700'
                    }`}
                  >
                    Associations
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && <PaginationControls />}
    </div>
  );
};

export default KnowledgeBaseTable;
